import { EmissionReport, EmissionReportStatus } from '@/api/EmissionReportModel'
import { FinalizedReport, FinalizedReportStatus } from './api/finalizedReports'
import useI18n from './use18n'
import { generatePeriodId, ReportingPeriodBasis } from '@/common'
import { eachDayOfInterval } from 'date-fns'

export type ProductionRouteStatus =
  | 'NOT_STARTED'
  | 'IN_PROGRESS'
  | 'READY_FOR_FINALIZATION'
  | 'REPORTED'

const productionRouteStatus: Record<string, ProductionRouteStatus> = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  READY_FOR_FINALIZATION: 'READY_FOR_FINALIZATION',
  REPORTED: 'REPORTED'
}

const getStatusForReportingPeriod = (
  reports: EmissionReport[],
  finalizedReports: FinalizedReport[],
  period: { startDate: Date; endDate: Date }
): ProductionRouteStatus => {
  const notDeletedReports = reports.filter(report => !report.deletedAt)

  const isPeriodFinalized = finalizedReports.some(finalizedReport => {
    return (
      new Date(finalizedReport.startDate) >= period.startDate &&
      new Date(finalizedReport.endDate) <= period.endDate &&
      finalizedReport.status === FinalizedReportStatus.SUBMITTED
    )
  })

  if (isPeriodFinalized) {
    return productionRouteStatus.REPORTED
  }
  const coverage = getInterimReportsMonthCoverage(
    notDeletedReports,
    period.startDate,
    period.endDate
  )

  const didOperatorReportInAllMonths = coverage.every(month => month.reportSubmitted)

  if (didOperatorReportInAllMonths) {
    return productionRouteStatus.READY_FOR_FINALIZATION
  }

  const didOperatorStartReportingInPeriod = coverage.some(
    month => month.reportDraft || month.reportSubmitted
  )

  if (didOperatorStartReportingInPeriod) {
    return productionRouteStatus.IN_PROGRESS
  }

  return productionRouteStatus.NOT_STARTED
}

const getReportingPeriodStatusDetails = (
  period: { startDate: Date; endDate: Date },
  reportsForProductionRoute: EmissionReport[],
  finalizedReportsForProductionRoute: FinalizedReport[],
  reportingPeriodBasis: ReportingPeriodBasis,
  dateLongFormatterMonthYear: (date: Date) => string
) => {
  const status = getStatusForReportingPeriod(
    reportsForProductionRoute,
    finalizedReportsForProductionRoute,
    period
  )

  const periodId = generatePeriodId(period.startDate, period.endDate)

  const label =
    reportingPeriodBasis === 'CALENDAR_YEAR'
      ? `${period.startDate.getFullYear()}`
      : `${dateLongFormatterMonthYear(period.startDate)} - ${dateLongFormatterMonthYear(period.endDate)}`

  return {
    periodId,
    label,
    status,
    startDate: period.startDate,
    endDate: period.endDate
  }
}

function getInterimReportsMonthCoverage(
  interimReports: EmissionReport[],
  rangeStart: Date,
  rangeEnd: Date
) {
  const months = eachDayOfInterval({
    start: rangeStart,
    end: rangeEnd
  }).filter(date => date.getDate() === 1) // Only keep first day of each month

  const currentDate = new Date()

  return months.map(monthDate => {
    const monthStr = monthDate.toISOString().split('T')[0].slice(0, 7) // YYYY-MM format

    // Check if any report covers this month (not just starts in this month)
    const reportsForMonth = interimReports.filter(report => {
      const reportStartDate = new Date(report.startDate)
      const reportEndDate = new Date(report.endDate)

      // A report covers this month if:
      // 1. The report starts before or during this month AND
      // 2. The report ends after or during this month
      return (
        reportStartDate <= new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0) && // report starts before or during this month
        reportEndDate >= monthDate // report ends after or during this month
      )
    })

    const reportingEnabled = monthDate <= currentDate
    const reportSubmitted = reportsForMonth.some(
      report => report.status === EmissionReportStatus.SUBMITTED
    )
    const reportDraft = reportsForMonth.some(report => report.status === EmissionReportStatus.DRAFT)

    return {
      month: monthStr,
      reportingEnabled,
      reportSubmitted,
      reportDraft
    }
  })
}

export default function useProductionRouteReportingStatus({
  productionRouteId,
  pivotDate,
  reportingPeriods,
  finalizedReports,
  reports,
  reportingPeriodBasis
}: {
  productionRouteId: string
  pivotDate: Date
  reportingPeriods: { startDate: Date; endDate: Date }[]
  finalizedReports: FinalizedReport[]
  reports: EmissionReport[]
  reportingPeriodBasis: ReportingPeriodBasis
}): {
  productionRouteId: string
  currentPeriod: {
    periodId: string
    label: string
    status: ProductionRouteStatus
    startDate: Date
    endDate: Date
  }
  previousPeriod: {
    periodId: string
    label: string
    status: ProductionRouteStatus
    startDate: Date
    endDate: Date
  } | null
} | null {
  const { dateLongFormatterMonthYear } = useI18n()

  const reportsForProductionRoute = reports.filter(report => {
    return report.productionRouteId === productionRouteId
  })
  const finalizedReportsForProductionRoute = finalizedReports.filter(report => {
    return report.productionRouteId === productionRouteId
  })

  const currentPeriodIndex = reportingPeriods.findIndex(period => {
    return period.startDate <= pivotDate && period.endDate >= pivotDate
  })

  if (currentPeriodIndex === -1) {
    return null
  }

  const currentPeriod = reportingPeriods[currentPeriodIndex]
  const previousPeriod = currentPeriodIndex > 0 ? reportingPeriods[currentPeriodIndex - 1] : null

  const currentPeriodDetails = getReportingPeriodStatusDetails(
    currentPeriod,
    reportsForProductionRoute,
    finalizedReportsForProductionRoute,
    reportingPeriodBasis,
    dateLongFormatterMonthYear
  )

  if (previousPeriod) {
    const previousPeriodDetails = getReportingPeriodStatusDetails(
      previousPeriod,
      reportsForProductionRoute,
      finalizedReportsForProductionRoute,
      reportingPeriodBasis,
      dateLongFormatterMonthYear
    )

    return {
      productionRouteId: productionRouteId,
      currentPeriod: currentPeriodDetails,
      previousPeriod: previousPeriodDetails
    }
  }

  return {
    productionRouteId: productionRouteId,
    currentPeriod: currentPeriodDetails,
    previousPeriod: null
  }
}
